import { useEffect, useState } from 'react';
import {
	BottomNavigation, Drawer, MobileETAUpdate, MobilePODUpload, MobileSearch,
	toast
} from '@components';
import { CARRIER_FOOTER_ACTION, FileUploadMeta, ShipmentTarget, YourLoadItem } from '@types';
import { usePodUnavailable, useUploadPOD } from '@api';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import UpdateOutlinedIcon from '@mui/icons-material/UpdateOutlined';
import { AxiosProgressEvent } from 'axios';

const navItems: {
	label: string;
	value: CARRIER_FOOTER_ACTION;
	icon: () => JSX.Element;
}[] = [
	{
		label: 'POD\'s',
		value: CARRIER_FOOTER_ACTION.UPLOAD_POD,
		icon: () => <FileUploadOutlinedIcon />,
	},
	{
		label: 'Search',
		value: CARRIER_FOOTER_ACTION.SEARCH_LOAD,
		icon: () => <SearchOutlinedIcon />,
	},
	{
		label: 'ETA\'s',
		value: CARRIER_FOOTER_ACTION.UPDATE_ETA,
		icon: () => <UpdateOutlinedIcon />,
	},
];

type TCarrierMobileFooterProps = {
	loadToUpdateETA: YourLoadItem | null;
	loadToUploadPOD: YourLoadItem | null;
	setLoadToUpdateETA?: React.Dispatch<React.SetStateAction<YourLoadItem | null>>;
	setLoadToUploadPOD: React.Dispatch<React.SetStateAction<YourLoadItem | null>>;
	onViewOnTheRoadLoadsClick?: () => void;
	onPODUploaded?: () => void
	activeMode: CARRIER_FOOTER_ACTION | null;
}

export const CarrierMobileFooter = (props: TCarrierMobileFooterProps) => {
	const {
		loadToUpdateETA,
		loadToUploadPOD,
		setLoadToUploadPOD,
		setLoadToUpdateETA,
		onViewOnTheRoadLoadsClick,
		onPODUploaded,
		activeMode: parentActiveMode
	} = props;
	const [isDrawerOpen, setIsDrawerOpen] = useState(false);
	const [isBackArrowVisible, setIsBackArrowVisible] = useState<boolean>(false);
	const [isBackArrowClicked, setIsBackArrowClicked] = useState<boolean>(false);
	const [activeMode, setActiveMode] = useState<CARRIER_FOOTER_ACTION | null>(null)
	const [shipmentTargets, setShipmentTargets] = useState<ShipmentTarget[]>([]);
	const [progressInfo, setProgressInfo] = useState<FileUploadMeta[]>([]);
	const [filesToUpload, setFilesToUpload] = useState<Record<string, FileUploadMeta[]>>({});
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [manifestIdToUpload, setManifestIdToUpload] = useState<string | null>(null);
	const { uploadPOD } = useUploadPOD();

	const closeDrawer = () => {
		setIsDrawerOpen(false);
		setActiveMode(null);
		setIsBackArrowVisible(false);
		setLoadToUploadPOD(null);
		setLoadToUpdateETA?.(null);
	}

	const handleNavigationClick = (index: number) => {
		setIsDrawerOpen(true);
		setActiveMode(navItems[index].value)
	}

	const { callPodUnavailable } = usePodUnavailable();
	const [isPODUnavailable, setIsPODUnavailable] = useState<boolean>(false);

	const handleUploads = async () => {
		if (!manifestIdToUpload) return;
		setIsLoading(true);
		if (isPODUnavailable) {
			try {
				await callPodUnavailable({ payload: { pod_status: 8 }, routes: [`${manifestIdToUpload}`] });
				setIsLoading(false);
				toast.success('POD Status has been updated successfully !', { position: 'bottom-center' })
			} catch (e:any) {
				toast.error(e?.message);
				console.log('error', e);
				setIsLoading(false);
			}
		} else {
			const files = filesToUpload[manifestIdToUpload] || [];
			if (files.length === 0) return;

			let hasAnyError = false;
			for (const file of files) {
				const form = new FormData();
				form.append('file', file.file);
				setProgressInfo((prev) =>
					prev.map((p) =>
						p.u_id === file.u_id ? { ...p, is_start: true } : p,
					),
				);
				try {
					await uploadPOD(`${manifestIdToUpload}`, form, {
						onUploadProgress: (e: AxiosProgressEvent) => {
							const pct = Math.round((e.loaded * 100) / (e.total || 1));
							setProgressInfo((prev) =>
								prev.map((p) =>
									p.u_id === file.u_id ? { ...p, percentage: pct } : p,
								),
							);
						},
					});
					setProgressInfo((prev) =>
						prev.map((p) =>
							p.u_id === file.u_id
								? {
									...p, percentage: 100, is_success: true
								}
								: p,
						),
					);
					setIsLoading(false);
					toast.success(`POD's successfully uploaded`, { position: 'bottom-center' })
				} catch (err: any) {
					hasAnyError = true;
					setProgressInfo((prev) =>
						prev.map((p) =>
							p.u_id === file.u_id
								? {
									...p,
									is_error: true,
									error_message: err?.message || 'Upload failed',
								}
								: p,
						),
					);
					setIsLoading(false);
				}

			}


			if (!hasAnyError) {
				setTimeout(() => {
					setFilesToUpload({});
					setProgressInfo([]);
					closeDrawer();
					onPODUploaded?.()
					setIsLoading(false);
				}, 1500);
			}
		}
	};

	useEffect(() => {
		if (loadToUpdateETA) {
			setIsDrawerOpen(true);
			setActiveMode(CARRIER_FOOTER_ACTION.UPDATE_ETA)
		}
	}, [loadToUpdateETA])

	useEffect(() => {
		if (loadToUploadPOD) {
			setIsDrawerOpen(true);
			setActiveMode(CARRIER_FOOTER_ACTION.UPLOAD_POD);
			setManifestIdToUpload(`${loadToUploadPOD.id}`);
		}
	}, [loadToUploadPOD])

	useEffect(() => {
		if (parentActiveMode) {
			setIsDrawerOpen(true);
			setActiveMode(parentActiveMode)
		}
	}, [parentActiveMode])
	return (
		<div>

			<BottomNavigation
				onItemClick={handleNavigationClick}
				navItems={navItems}
			/>
			<Drawer
				open={isDrawerOpen}
				toggleDrawer={closeDrawer}
				height={'90%'}
				isBackArrowVisible={isBackArrowVisible}
				onBackArrowClick={() => setIsBackArrowClicked(true)}
				footerActionBtnText='Save'
				footerCancelBtnText='Cancel'
				onActionBtnClick={handleUploads}
				onCancelBtnClick={closeDrawer}
				isLoading={isLoading}
				isFooterVisible={activeMode === CARRIER_FOOTER_ACTION.UPLOAD_POD && Object.keys(filesToUpload)?.length > 0}
				canCloseWithSwipe={false}
			>
				{
					activeMode === CARRIER_FOOTER_ACTION.UPLOAD_POD && (
						<>
							<MobilePODUpload
								shipmentTargets={shipmentTargets}
								onSetShipmentTargets={setShipmentTargets}
								filesToUpload={filesToUpload}
								onSetFilesToUpload={setFilesToUpload}
								onViewOnTheRoadLoadsClick={() => {
									onViewOnTheRoadLoadsClick?.();
									closeDrawer();
								}}
								setIsPODUnavailable={setIsPODUnavailable}
								progressInfo={progressInfo}
								setProgressInfo={setProgressInfo}
								setIsBackArrowVisible={setIsBackArrowVisible}
								isBackArrowClicked={isBackArrowClicked}
								setIsBackArrowClicked={setIsBackArrowClicked}
								loadToUploadPOD={loadToUploadPOD}
								setManifestIdToUpload={setManifestIdToUpload}
							/>
						</>
					)
				}
				{
					activeMode === CARRIER_FOOTER_ACTION.SEARCH_LOAD && (
						<MobileSearch />
					)
				}
				{
					activeMode === CARRIER_FOOTER_ACTION.UPDATE_ETA && (
						<MobileETAUpdate
							setIsBackArrowVisible={setIsBackArrowVisible}
							isBackArrowClicked={isBackArrowClicked}
							setIsBackArrowClicked={setIsBackArrowClicked}
							loadToUpdateETA={loadToUpdateETA}
						/>
					)
				}
			</Drawer>
		</div>
	);
}
